<template>
  <v-container fluid class="ncrm-contacts">
    <div class="ncrm-contacts__header" data-tour="contacts-header">
      <div class="ncrm-contacts__header-left">
        <v-icon class="ncrm-contacts__header-icon" size="16">mdi-account-multiple-outline</v-icon>
        <h1 class="ncrm-contacts__title">{{ $t("ncrm.contacts.title") }}</h1>
        <span class="ncrm-contacts__subtitle">{{ $t("ncrm.contacts.subtitle") }}</span>
      </div>
      <div class="ncrm-contacts__header-right">
        <NcrmContactsAdvancedFilter
          :model-value="advancedFilters"
          v-model:open="advancedFilterOpen"
          :type="activeType" class="ncrm-contacts__filter-trigger" data-tour="contacts-advanced-filter"
          @update:model-value="handleAdvancedFiltersUpdate"
        />
        <v-text-field
          v-model="search"
          :label="$t('ncrm.contacts.search')"
          density="compact"
          hide-details
          prepend-inner-icon="mdi-magnify"
          class="ncrm-contacts__search" data-tour="contacts-search"
        />
        <v-btn color="primary" variant="tonal" @click="handleImport">
          <v-icon start size="16">mdi-upload</v-icon>
          {{ $t("ncrm.contacts.import") }}
        </v-btn>
        <v-btn color="primary" data-tour="contacts-create" @click="handleAdd">
          <v-icon start size="16">mdi-plus</v-icon>
          {{ $t("ncrm.contacts.add") }}
        </v-btn>
        <PageHelpButton />
      </div>
    </div>
    <v-card class="mt-4">
      <NcrmPreferredViewTabs
        v-model="activeTab" data-tour="contacts-views"
        :items="viewItems"
        :preferred-value="preferredTab"
        :aria-label="$t('ncrm.shared.viewPreferences.tabsAriaLabel')"
        class="px-4 pt-4"
        @set-default="setPreferredTab"
      />
      <NcrmContactsFilterBar
        v-if="hasActiveAdvancedFilters" :model-value="advancedFilters"
        class="px-4 pt-3"
        @edit="advancedFilterOpen = true"
        @remove="removeAdvancedFilterCondition"
        @clear="clearAdvancedFilters"
      />
      <NcrmContactsBulkBar
        :count="selectedContactCount" :loading="bulkExportLoading"
        :ids="selectedContactIds" :subject-type="activeType" :records="filteredItems"
        :company-ids="activeTab === 'companies' ? selectedContactIds : []" @converted="handleCompaniesConverted"
        @download="handleBulkDownloadSelected" @clear="clearSelectedContacts" @sent="fetchContacts"
      />
      <v-card-text class="pa-0">
        <v-data-table-server
          v-model="selectedContactIds"
          :headers="headers"
          :items="filteredItems"
          :items-length="itemsLength"
          item-key="id"
          item-value="id"
          show-select
          select-strategy="page"
          class="ncrm-contacts-table elevation-0"
          v-model:page="page"
          v-model:items-per-page="itemsPerPage"
          v-model:sort-by="sortBy"
          :items-per-page-options="itemsPerPageOptions"
          :loading="tableLoading"
          density="comfortable"
          @click:row="handleContactRowClick"
        >
          <template #item.name="{ item }">
            <div class="d-flex align-center ga-2">
              <v-icon size="15" class="ncrm-contacts-table__cell-icon">
                {{ activeTab === 'companies' ? 'mdi-domain' : 'mdi-account-outline' }}
              </v-icon>
              <span class="text-body-2 ncrm-text-strong">{{ item.name }}</span>
              <span class="ncrm-contacts-table__privacy-mobile">
                <NcrmPrivacyStatusChip
                  v-bind="privacyChipProps(item)"
                  interactive
                  @activate="goToDataProtectionTab(item)"
                />
              </span>
            </div>
          </template>
          <template #item.company="{ item }">
            <div v-if="item.company" class="d-flex align-center ga-2 ncrm-contacts-table__cell-muted">
              <v-icon size="14" class="ncrm-contacts-table__cell-icon">mdi-domain</v-icon>
              <span>{{ item.company }}</span>
            </div>
            <span v-else class="ncrm-contacts-table__empty-dash">—</span>
          </template>
          <template #item.mailjet_status="{ item }">
            <v-tooltip location="top">
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  :color="item.in_mailjet ? 'primary' : 'grey-lighten-1'"
                  size="18"
                  class="ncrm-contacts-table__mailjet-icon"
                  @click.stop="goToMailjetTab(item)"
                >
                  mdi-email-fast-outline
                </v-icon>
              </template>
              <span>{{
                item.in_mailjet
                  ? $t("ncrm.contacts.table.mailjet.active")
                  : $t("ncrm.contacts.table.mailjet.inactive")
              }}</span>
            </v-tooltip>
          </template>
          <template #item.last_call="{ item }">
            <NcrmLastCallCell :last-call-at="item.last_call_at" :has-pbx="item.last_call_has_pbx" />
          </template>
          <template #item.privacy_state="{ item }">
            <NcrmPrivacyStatusChip
              v-bind="privacyChipProps(item)"
              interactive
              @activate="goToDataProtectionTab(item)"
            />
          </template>
          <template #item.email="{ item }">
            <a
              v-if="item.email"
              :href="`mailto:${item.email}`"
              class="ncrm-contacts-table__link"
              @click.stop
            >
              {{ item.email }}
            </a>
            <span v-else class="ncrm-contacts-table__empty-dash">—</span>
          </template>
          <template #item.phone="{ item }">
            <NcrmPrivacyPhoneLink
              v-if="item.phone"
              :phone="item.phone"
              class="ncrm-contacts-table__link"
            >
              <v-icon size="14" class="ncrm-contacts-table__link-icon">mdi-phone-outline</v-icon>
              {{ item.phone }}
            </NcrmPrivacyPhoneLink>
            <span v-else class="ncrm-contacts-table__empty-dash">—</span>
          </template>
          <template #item.category="{ item }">
            <div v-if="item.category" class="d-flex align-center ga-1 ncrm-contacts-table__cell-muted">
              <v-icon size="13" class="ncrm-contacts-table__cell-icon">mdi-tag-outline</v-icon>
              <span>{{ item.category }}</span>
            </div>
            <span v-else class="ncrm-contacts-table__empty-dash">—</span>
          </template>
          <template #item.source="{ item }">
            <div v-if="item.source" class="d-flex align-center ga-1 ncrm-contacts-table__cell-muted">
              <v-icon size="13" class="ncrm-contacts-table__cell-icon">mdi-import</v-icon>
              <span>{{ item.source }}</span>
            </div>
            <span v-else class="ncrm-contacts-table__empty-dash">—</span>
          </template>
          <template #item.createdAt="{ item }">
            <div class="d-flex align-center ga-1 ncrm-contacts-table__date">
              <v-icon size="13" class="ncrm-contacts-table__date-icon">mdi-clock-outline</v-icon>
              {{ item.createdAt }}
            </div>
          </template>
          <template #item.row_nav>
            <v-icon size="18" class="ncrm-contacts-table__chevron">mdi-chevron-right</v-icon>
          </template>
          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.contacts.noData") }}
            </v-alert>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>
    <NcrmRecordEditDialog
      v-model="addDialog"
      :title="
        activeTab === 'companies'
          ? $t('ncrm.contacts.addCompanyTitle')
          : $t('ncrm.contacts.addPersonTitle')
      "
      :subtitle="$t('ncrm.contacts.subtitle')"
      :loading="addLoading"
      :max-width="720"
      :extra-action-label="$t('ncrm.contacts.details.dialogs.completeAllValues')"
      :extra-action-disabled="addShowAllFields || addLoading"
      @extra-action="expandAddFormFields"
      @save="submitAdd"
    >
      <v-row>
        <template v-if="activeTab === 'companies'">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="addForm.name"
              :label="$t('ncrm.contacts.form.companyName')"
              required
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="addForm.businessCategory"
              :label="$t('ncrm.contacts.form.businessCategory')"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="addForm.emailPrimary"
              :label="$t('ncrm.contacts.form.emailPrimary')"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="addForm.phonePrimary"
              :label="$t('ncrm.contacts.form.phonePrimary')"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="addForm.source"
              :label="$t('ncrm.contacts.form.source')"
            />
          </v-col>
        </template>
        <template v-else>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="addForm.firstName"
              :label="$t('ncrm.contacts.form.firstName')"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="addForm.lastName"
              :label="$t('ncrm.contacts.form.lastName')"
              required
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="addForm.emailPrimary"
              :label="$t('ncrm.contacts.form.emailPrimary')"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="addForm.phonePrimary"
              :label="$t('ncrm.contacts.form.phonePrimary')"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="addForm.ncrmCompanyId"
              v-model:search="companySearchQuery"
              :items="companyOptions"
              :loading="companyOptionsLoading"
              item-title="title"
              item-value="value"
              :label="$t('ncrm.contacts.form.companyLink')"
              clearable
              autocomplete="new-password"
              :menu-props="{ maxHeight: 320, zIndex: 2900 }"
              no-filter
              @focus="ensureCompanyOptionsLoaded"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="addForm.source"
              :label="$t('ncrm.contacts.form.source')"
            />
          </v-col>
        </template>
        <template v-if="addShowAllFields">
          <v-col cols="12">
            <div class="text-caption text-medium-emphasis">
              {{ $t("ncrm.contacts.details.dialogs.additionalFields") }}
            </div>
          </v-col>
          <v-col
            v-for="field in addAdvancedFields"
            :key="`add-advanced-field-${activeType}-${field.key}`"
            cols="12"
            md="6"
          >
            <component
              :is="field.component"
              v-model="addForm[field.key]"
              v-bind="field.props"
            />
          </v-col>
        </template>
        <v-col cols="12">
          <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
            <div class="text-caption text-medium-emphasis">
              {{
                activeTab === "companies"
                  ? $t("ncrm.contacts.details.company.fields.customFields")
                  : $t("ncrm.contacts.details.person.fields.customFields")
              }}
            </div>
            <v-btn
              size="x-small"
              variant="text"
              color="primary"
              class="px-0"
              prepend-icon="mdi-plus"
              :disabled="addLoading"
              @click="addAddCustomFieldRow"
            >
              {{ $t("ncrm.contacts.details.customFieldEditor.addAction") }}
            </v-btn>
          </div>
          <v-row>
            <v-col
              v-for="entry in addNewCustomFields"
              :key="`add-new-custom-field-${entry.temp_id}`"
              cols="12"
            >
              <v-row dense class="ncrm-custom-field-editor-row">
                <v-col cols="12" md="5">
                  <v-text-field
                    v-model="entry.label"
                    :label="$t('ncrm.contacts.details.customFieldEditor.nameLabel')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="entry.value"
                    :label="$t('ncrm.contacts.details.customFieldEditor.valueLabel')"
                  />
                </v-col>
                <v-col cols="12" md="1" class="d-flex align-center justify-end pb-md-4">
                  <v-btn
                    icon="mdi-close"
                    size="small"
                    variant="text"
                    color="error"
                    :disabled="addLoading"
                    :aria-label="$t('ncrm.contacts.details.customFieldEditor.removeAction')"
                    @click="removeAddCustomFieldRow(entry.temp_id)"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </NcrmRecordEditDialog>
    <v-dialog v-model="importCustomFieldDialog.show" max-width="520">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="text-h6">{{ $t("ncrm.importWizard.createCustomFieldTitle") }}</div>
          <v-btn icon @click="closeImportCustomFieldDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-text-field
            v-model="importCustomFieldDialog.label"
            :label="$t('ncrm.importWizard.createCustomFieldLabel')"
            autofocus
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-space-between">
          <v-btn variant="text" @click="closeImportCustomFieldDialog">
            {{ $t("ncrm.importWizard.cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="importCustomFieldDialog.saving"
            @click="submitImportCustomField"
          >
            {{ $t("ncrm.contacts.form.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="importDialog" max-width="1100">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div>
            <div class="text-h6">{{ $t("ncrm.importWizard.title") }}</div>
          </div>
          <v-btn icon @click="closeImportDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-stepper v-model="step" elevation="0">
            <v-stepper-header class="ncrm-import-stepper-header">
              <v-stepper-item :title="$t('ncrm.importWizard.steps.type')" :value="1" />
              <v-stepper-item :title="$t('ncrm.importWizard.steps.upload')" :value="2" />
              <v-stepper-item :title="$t('ncrm.importWizard.steps.mapping')" :value="3" />
              <v-stepper-item :title="$t('ncrm.importWizard.steps.options')" :value="4" />
              <v-stepper-item :title="$t('ncrm.importWizard.steps.preview')" :value="5" />
              <v-stepper-item :title="$t('ncrm.importWizard.steps.provenance')" :value="6" />
            </v-stepper-header>
            <v-stepper-window class="ncrm-stepper-window">
              <v-stepper-window-item :value="1" class="ncrm-stepper-item">
                <div>
                  <v-row>
                  <v-col cols="12" md="6">
                    <v-card variant="outlined" class="pa-4">
                      <div class="text-subtitle-1 font-weight-semibold">
                        {{ $t("ncrm.importWizard.chooseType") }}
                      </div>
                      <v-radio-group v-model="importType" class="mt-3">
                        <v-radio :label="$t('ncrm.importWizard.types.people')" value="person" />
                        <v-radio :label="$t('ncrm.importWizard.types.companies')" value="company" />
                        <v-radio :label="$t('ncrm.importWizard.types.messages')" value="activity" />
                      </v-radio-group>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-alert type="info">
                      {{ $t("ncrm.importWizard.typeHint") }}
                    </v-alert>
                  </v-col>
                  </v-row>
                </div>
              </v-stepper-window-item>
              <v-stepper-window-item :value="2" class="ncrm-stepper-item">
                <div>
                  <v-row>
                  <v-col cols="12" md="6">
                    <v-file-input
                      v-model="importFile"
                      :label="$t('ncrm.importWizard.file')"
                      prepend-icon="mdi-paperclip"
                      accept=".csv,.xlsx,.json,.sql"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="importName"
                      :label="$t('ncrm.importWizard.importName')"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="importSource"
                      :label="$t('ncrm.importWizard.source')"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="importTags"
                      :label="$t('ncrm.importWizard.tags')"
                    />
                  </v-col>
                  </v-row>
                  <v-alert v-if="previewLoading" type="info" class="mt-2">
                    {{ $t("ncrm.importWizard.previewLoading") }}
                  </v-alert>
                </div>
              </v-stepper-window-item>
              <v-stepper-window-item :value="3" class="ncrm-stepper-item">
                <div class="d-flex flex-wrap align-center justify-space-between mb-4 ga-3">
                  <div>
                    <div class="text-subtitle-1 font-weight-semibold">
                      {{ $t("ncrm.importWizard.mappingTitle") }}
                    </div>
                    <div class="text-body-2 text-medium-emphasis">
                      {{ $t("ncrm.importWizard.mappingHint") }}
                    </div>
                  </div>
                  <div class="d-flex ga-2">
                    <v-btn variant="tonal" color="primary" @click="autoMap">
                      {{ $t("ncrm.importWizard.autoMap") }}
                    </v-btn>
                    <v-btn variant="outlined" class="ncrm-reset-btn" @click="resetMap">
                      {{ $t("ncrm.importWizard.resetMap") }}
                    </v-btn>
                  </div>
                </div>
                <v-alert
                  v-if="importType === 'activity'"
                  type="info"
                  class="mb-4"
                >
                  <div class="font-weight-semibold">
                    {{ $t("ncrm.importWizard.activityMappingGuide.title") }}
                  </div>
                  <div class="text-body-2 mt-1 mb-2">
                    {{ $t("ncrm.importWizard.activityMappingGuide.subtitle") }}
                  </div>
                  <v-table density="compact" class="ncrm-import-guide-table">
                    <thead>
                      <tr>
                        <th>{{ $t("ncrm.importWizard.activityGuideHeaders.fileColumn") }}</th>
                        <th>{{ $t("ncrm.importWizard.activityGuideHeaders.selectInMapping") }}</th>
                        <th>{{ $t("ncrm.importWizard.activityGuideHeaders.role") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in activityMappingGuideRows" :key="row.key">
                        <td>{{ row.fileColumn }}</td>
                        <td>{{ row.mappingTarget }}</td>
                        <td>{{ row.purpose }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-alert>
                <v-data-table
                  :headers="mappingHeaders"
                  :items="mappingRows"
                  class="elevation-0 ncrm-contacts__dialog-table"
                  density="comfortable"
                >
                  <template #item.mappedField="{ item }">
                    <v-autocomplete
                      v-model="item.mappedField"
                      :items="mappingFieldOptionsForRow(item)"
                      item-title="title"
                      item-value="value"
                      density="compact"
                      hide-details
                      clearable
                      :menu-props="{ maxHeight: 360 }"
                      @update:model-value="handleMappingFieldChange(item, $event)"
                    />
                  </template>
                </v-data-table>
              </v-stepper-window-item>
              <v-stepper-window-item :value="4" class="ncrm-stepper-item">
                <v-row class="mb-1">
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="legacyReferencePrefix"
                      :label="$t('ncrm.importWizard.legacyReferencePrefix')"
                      :placeholder="$t('ncrm.importWizard.legacyReferencePrefixPlaceholder')"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-alert type="info">
                      {{ $t("ncrm.importWizard.legacyReferencePrefixHint") }}
                    </v-alert>
                  </v-col>
                </v-row>
                <div v-if="importType === 'activity'">
                  <v-row>
                    <v-col cols="12">
                      <v-alert type="info" class="mb-1">
                        <div class="font-weight-semibold">
                          {{ $t("ncrm.importWizard.activityOptionsGuide.title") }}
                        </div>
                        <div class="text-body-2 mt-1 mb-2">
                          {{ $t("ncrm.importWizard.activityOptionsGuide.subtitle") }}
                        </div>
                        <v-table density="compact" class="ncrm-import-guide-table">
                          <thead>
                            <tr>
                              <th>{{ $t("ncrm.importWizard.activityGuideHeaders.option") }}</th>
                              <th>{{ $t("ncrm.importWizard.activityGuideHeaders.role") }}</th>
                              <th>{{ $t("ncrm.importWizard.activityGuideHeaders.recommended") }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="row in activityOptionsGuideRows" :key="row.key">
                              <td>{{ row.optionLabel }}</td>
                              <td>{{ row.purpose }}</td>
                              <td>{{ row.recommended }}</td>
                            </tr>
                          </tbody>
                        </v-table>
                        <div class="text-body-2 mt-2">
                          {{ $t("ncrm.importWizard.activityOptionsGuide.conflictNote") }}
                        </div>
                      </v-alert>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="activityTargetEntityType"
                        :items="activityTargetEntityOptions"
                        :label="$t('ncrm.importWizard.activityTargetEntityType')"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="activityMatchSourceColumn"
                        :items="mappingColumnOptions"
                        :label="$t('ncrm.importWizard.activityMatchSourceColumn')"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="activityMatchInternalField"
                        :items="activityMatchFieldOptions"
                        item-title="title"
                        item-value="value"
                        :label="$t('ncrm.importWizard.activityMatchInternalField')"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="activityDedupeSourceColumn"
                        :items="mappingColumnOptions"
                        :label="$t('ncrm.importWizard.activityDedupeSourceColumn')"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="activityDedupeInternalField"
                        :items="activityDedupeFieldOptions"
                        item-title="title"
                        item-value="value"
                        :label="$t('ncrm.importWizard.activityDedupeInternalField')"
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-alert type="info">
                        {{ $t("ncrm.importWizard.activityMatchHint") }}
                      </v-alert>
                    </v-col>
                  </v-row>
                </div>
                <div v-else>
                  <v-row>
                    <v-col cols="12">
                      <v-alert type="info" class="mb-1">
                        <div class="font-weight-semibold">
                          {{ $t("ncrm.importWizard.contactMatchGuide.title") }}
                        </div>
                        <div class="text-body-2 mt-1">
                          {{ $t("ncrm.importWizard.contactMatchGuide.subtitle") }}
                        </div>
                        <div class="text-body-2 mt-2">
                          {{ $t("ncrm.importWizard.contactMatchGuide.example") }}
                        </div>
                      </v-alert>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="contactMatchSourceColumn"
                        :items="mappingColumnOptions"
                        :label="$t('ncrm.importWizard.contactMatchSourceColumn')"
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="contactMatchInternalField"
                        :items="contactMatchFieldOptions"
                        item-title="title"
                        item-value="value"
                        :label="$t('ncrm.importWizard.contactMatchInternalField')"
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-combobox
                        v-model="businessCategory"
                        :items="businessCategoryOptions"
                        :label="$t('ncrm.importWizard.businessCategory')"
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-alert type="info">
                        {{ $t("ncrm.importWizard.categoryHint") }}
                      </v-alert>
                    </v-col>
                  </v-row>
                </div>
              </v-stepper-window-item>
              <v-stepper-window-item :value="5" class="ncrm-stepper-item">
                <div class="text-subtitle-1 font-weight-semibold mb-2">
                  {{ $t("ncrm.importWizard.previewTitle") }}
                </div>
                <v-data-table
                  :headers="previewHeaders"
                  :items="previewRows"
                  class="elevation-0 ncrm-contacts__dialog-table"
                  density="comfortable"
                />
                <v-divider class="my-4" />
                <v-select
                  v-model="conflictStrategy"
                  :items="conflictOptions"
                  :label="$t('ncrm.importWizard.conflictStrategy')"
                />
                <v-alert v-if="previewLoading" type="info" class="mt-2">
                  {{ $t("ncrm.importWizard.previewLoading") }}
                </v-alert>
              </v-stepper-window-item>
              <v-stepper-window-item :value="6" class="ncrm-stepper-item">
                <v-alert type="success" class="mb-4 mt-2">
                  {{ $t("ncrm.importWizard.ready") }}
                </v-alert>
                <!-- Ultimul pas: momentul în care importul chiar se comite. -->
                <NcrmImportProvenanceStep
                  v-model="importProvenance"
                  :show-errors="provenanceTouched"
                />
                <div class="text-body-2 text-medium-emphasis mt-4">
                  {{ $t("ncrm.importWizard.finishHint") }}
                </div>
              </v-stepper-window-item>
            </v-stepper-window>
          </v-stepper>
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-space-between">
          <v-btn variant="text" @click="prevStep" :disabled="step === 1">
            {{ $t("ncrm.importWizard.back") }}
          </v-btn>
          <div class="d-flex ga-2">
            <v-btn variant="text" class="ncrm-cancel-btn" @click="closeImportDialog">
              {{ $t("ncrm.importWizard.cancel") }}
            </v-btn>
            <v-btn color="primary" :loading="executeLoading" @click="nextStep">
              {{ step === 6 ? $t("ncrm.importWizard.finish") : $t("ncrm.importWizard.next") }}
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snackbar.show" :timeout="3000" color="info">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>
<script>
import axios from "axios";
import NcrmContactsBulkBar from "@/components/ncrm/contacts/NcrmContactsBulkBar.vue";
import NcrmContactsAdvancedFilter from "@/components/ncrm/contacts/NcrmContactsAdvancedFilter.vue";
import NcrmContactsFilterBar from "@/components/ncrm/contacts/NcrmContactsFilterBar.vue";
import NcrmImportProvenanceStep from "@/components/ncrm/contacts/NcrmImportProvenanceStep.vue";
import {
  createEmptyProvenance,
  isProvenanceComplete,
} from "@/components/ncrm/contacts/importProvenance.mjs";
import {
  appendImportConfig,
  normalizeLegacyReferencePrefix,
} from "@/components/ncrm/contacts/importFormData.mjs";
import {
  hasContactsAdvancedFilters,
  normalizeContactsAdvancedFilters,
  readContactsAdvancedFilters,
  writeContactsAdvancedFilters,
} from "@/Utile/ncrmContactsAdvancedFilter.mjs";
import NcrmLastCallCell from "@/components/ncrm/shared/NcrmLastCallCell.vue";
import NcrmPrivacyStatusChip from "@/components/ncrm/shared/NcrmPrivacyStatusChip.vue";
import NcrmPrivacyPhoneLink from "@/components/ncrm/shared/NcrmPrivacyPhoneLink.vue";
import NcrmPreferredViewTabs from "@/components/ncrm/shared/NcrmPreferredViewTabs.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import PageHelpButton from "@/components/tours/PageHelpButton.vue";
import { buildContactsTableHeaders } from "@/components/ncrm/contacts/contactsTableHeaders.mjs";
import { resolveNcrmContactDetailsRoute } from "@/Utile/ncrmRouting.mjs";
import { buildNcrmContactLabelOptions, isNcrmContactLabelField, resolveNcrmContactsTab } from "@/Utile/ncrmRecordDetails.mjs";
import {
  loadPreferredView,
  resolveViewPreferencePageKey,
  savePreferredView,
} from "@/Utile/ncrmViewPreferences.mjs";
import { contactBulkExportMixin } from "./contactBulkExport.mjs";
import { contactListMixin, createDefaultContactsSortBy } from "./contactListMixin.mjs";
import {
  COMPANY_ADD_ADVANCED_FIELD_KEYS,
  PERSON_ADD_ADVANCED_FIELD_KEYS,
  COMPANY_ADD_FIELD_TRANSLATION_KEYS,
  PERSON_ADD_FIELD_TRANSLATION_KEYS,
  COMPANY_IMPORT_MAPPING_FIELD_KEYS,
  PERSON_IMPORT_MAPPING_FIELD_KEYS,
  IMPORT_WIZARD_FIELD_TRANSLATION_KEYS,
} from "./contactFieldCatalog.mjs";
const CONTACT_VIEW_ITEMS = Object.freeze([{ value: "companies" }, { value: "people" }]);
const CONTACTS_PAGE_KEY_FALLBACK = "/ncrm/contacts";
export default {
  name: "NcrmContacts",
  mixins: [contactBulkExportMixin, contactListMixin],
  components: {
    NcrmContactsBulkBar,
    NcrmContactsAdvancedFilter,
    NcrmContactsFilterBar,
    NcrmImportProvenanceStep,
    NcrmLastCallCell,
    NcrmPrivacyStatusChip,
    NcrmPrivacyPhoneLink,
    NcrmPreferredViewTabs,
    NcrmRecordEditDialog,
    PageHelpButton,
  },
  data() {
    const preferredTab =
      loadPreferredView({
        pageKey: resolveViewPreferencePageKey(this.$route, CONTACTS_PAGE_KEY_FALLBACK),
        items: CONTACT_VIEW_ITEMS,
        fallback: "companies",
      }) || "companies";
    const initialTab = resolveNcrmContactsTab(this.$route?.query?.tab, preferredTab);
    const initialType = initialTab === "companies" ? "company" : "person";
    return {
      activeTab: initialTab,
      preferredTab,
      // Filtre avansate persistate per tip (company/person) până la logout
      // sau ștergere manuală — vezi Utile/ncrmContactsAdvancedFilter.mjs.
      advancedFilters: readContactsAdvancedFilters(initialType),
      advancedFilterOpen: false,
      search: "",
      page: 1,
      itemsPerPage: 25,
      sortBy: createDefaultContactsSortBy(),
      totalItems: 0,
      tableLoading: false,
      people: [],
      companies: [],
      companyOptions: [],
      companyOptionsLoading: false,
      companyOptionsRequestSeq: 0,
      companySearchQuery: "",
      companySearchDebounceId: null,
      addDialog: false,
      addLoading: false,
      addShowAllFields: false,
      addNewCustomFields: [],
      addNewCustomFieldSeq: 0,
      customFieldsLoading: false,
      customFieldDefinitions: {
        person: [],
        company: [],
      },
      importCustomFieldDialog: {
        show: false,
        saving: false,
        label: "",
        targetColumn: null,
      },
      importCreatedCustomFieldIds: {
        person: [],
        company: [],
      },
      createCustomFieldOptionValue: "__create_custom_field__",
      addForm: {
        firstName: "",
        lastName: "",
        emailPrimary: "",
        phonePrimary: "",
        ncrmCompanyId: null,
        name: "",
        businessCategory: "",
        source: "",
        old_reference: "",
        legal_name: "",
        fiscal_code: "",
        vat_number: "",
        registration_number: "",
        iban: "",
        industry: "",
        company_size: "",
        employees_count: "",
        annual_revenue: "",
        email_secondary: "",
        email_tertiary: "",
        email_primary_label: "",
        email_secondary_label: "",
        email_tertiary_label: "",
        phone_secondary: "",
        phone_tertiary: "",
        phone_primary_label: "",
        phone_secondary_label: "",
        phone_tertiary_label: "",
        website: "",
        address_text: "", country: "",
        linkedin: "",
        facebook: "",
        instagram: "",
        administrator_name: "",
        sales_manager_id: "",
        tags: "",
        notes: "",
        owner_user_id: "",
        position: "",
        department: "",
      },
      importDialog: false,
      step: 1,
      importType: "person",
      importFile: null,
      importName: "",
      // DPIA A3 / B1 — răspunsul despre proveniență, cerut în ultimul pas.
      importProvenance: createEmptyProvenance(),
      provenanceTouched: false,
      importSource: "",
      importTags: "",
      businessCategory: "",
      legacyReferencePrefix: "",
      contactMatchSourceColumn: "",
      contactMatchInternalField: "",
      activityTargetEntityType: "company",
      activityMatchSourceColumn: "",
      activityMatchInternalField: "",
      activityDedupeSourceColumn: "",
      activityDedupeInternalField: "",
      conflictStrategy: "per_field",
      mappingRows: [
        { column: "Email", sample: "ana@example.com", mappedField: "email_primary" },
        { column: "Telefon", sample: "+373 600 000", mappedField: "phone_primary" },
        { column: "Nume", sample: "Popa", mappedField: "last_name" },
      ],
      businessCategoryOptions: ["IT", "Retail", "HoReCa"],
      previewRawRows: [],
      previewMappedRows: [],
      previewRows: [],
      previewLoading: false,
      previewReady: false,
      executeLoading: false,
      snackbar: {
        show: false,
        message: "",
      },
    };
  },
  computed: {
    viewItems() {
      return [
        { value: "companies", label: this.$t("ncrm.contacts.tabs.companies") },
        { value: "people", label: this.$t("ncrm.contacts.tabs.people") },
      ];
    },
    headers() {
      return buildContactsTableHeaders({ t: (key) => this.$t(key), activeTab: this.activeTab });
    },
    filteredItems() {
      const source = this.activeTab === "companies" ? this.companies : this.people;
      return source;
    },
    itemsLength() {
      return this.totalItems || this.filteredItems.length;
    },
    itemsPerPageOptions() {
      return [
        { title: "25", value: 25 },
        { title: "50", value: 50 },
        { title: "100", value: 100 },
        { title: this.$t("ncrm.contacts.all"), value: -1 },
      ];
    },
    mappingHeaders() {
      return [
        { title: this.$t("ncrm.importWizard.mappingHeaders.column"), key: "column" },
        { title: this.$t("ncrm.importWizard.mappingHeaders.sample"), key: "sample" },
        { title: this.$t("ncrm.importWizard.mappingHeaders.mapped"), key: "mappedField" },
      ];
    },
    mappingFieldOptions() {
      const options = [];
      const fieldKeys =
        this.importType === "company"
          ? COMPANY_IMPORT_MAPPING_FIELD_KEYS
          : this.importType === "activity"
            ? [
                "content",
                "title",
                "happened_at",
                "due_at",
                "old_reference",
                "legacy_source",
                "legacy_author_name",
                "owner_user_id",
                "type_payload",
                "legacy_payload",
              ]
            : PERSON_IMPORT_MAPPING_FIELD_KEYS;
      fieldKeys.forEach((fieldKey) => {
        options.push({
          title: this.resolveImportMappingFieldLabel(fieldKey),
          value: fieldKey,
        });
      });
      if (this.importType === "activity") {
        return options;
      }
      const createdIds = new Set(
        (this.importCreatedCustomFieldIds?.[this.importType] || []).map((value) => String(value))
      );
      const customFields = (this.customFieldDefinitions[this.importType] || []).filter((field) =>
        createdIds.has(String(field.id))
      );
      customFields.forEach((field) => {
        options.push({
          title: `${field.label} (${this.$t("ncrm.importWizard.fields.customSuffix")})`,
          value: `custom_field_${field.id}`,
        });
      });
      options.push({
        title: this.$t("ncrm.importWizard.fields.createCustomField"),
        value: this.createCustomFieldOptionValue,
      });
      return options;
    },
    mappingColumnOptions() {
      return this.mappingRows.map((row) => row.column).filter(Boolean);
    },
    contactMatchFieldOptions() {
      if (this.importType === "person") {
        return [
          { title: this.$t("ncrm.importWizard.fields.oldReference"), value: "old_reference" },
          { title: this.$t("ncrm.importWizard.fields.emailPrimary"), value: "email_primary" },
          { title: this.$t("ncrm.importWizard.fields.phonePrimary"), value: "phone_primary" },
          { title: this.$t("ncrm.importWizard.fields.firstName"), value: "first_name" },
          { title: this.$t("ncrm.importWizard.fields.lastName"), value: "last_name" },
          { title: this.$t("ncrm.importWizard.fields.source"), value: "source" },
          { title: "ID", value: "id" },
        ];
      }
      return [
        { title: this.$t("ncrm.importWizard.fields.oldReference"), value: "old_reference" },
        { title: this.$t("ncrm.importWizard.fields.companyName"), value: "name" },
        { title: this.$t("ncrm.importWizard.fields.vatNumber"), value: "vat_number" },
        { title: this.$t("ncrm.importWizard.fields.emailPrimary"), value: "email_primary" },
        { title: this.$t("ncrm.importWizard.fields.phonePrimary"), value: "phone_primary" },
        { title: this.$t("ncrm.importWizard.fields.businessCategory"), value: "business_category" },
        { title: this.$t("ncrm.importWizard.fields.source"), value: "source" },
        { title: "ID", value: "id" },
      ];
    },
    activityTargetEntityOptions() {
      return [
        {
          title: this.$t("ncrm.importWizard.activityTargetEntityTypes.company"),
          value: "company",
        },
        {
          title: this.$t("ncrm.importWizard.activityTargetEntityTypes.person"),
          value: "person",
        },
      ];
    },
    activityMatchFieldOptions() {
      if (this.activityTargetEntityType === "person") {
        return [
          { title: this.$t("ncrm.importWizard.fields.oldReference"), value: "old_reference" },
          { title: this.$t("ncrm.importWizard.fields.firstName"), value: "first_name" },
          { title: this.$t("ncrm.importWizard.fields.lastName"), value: "last_name" },
          { title: this.$t("ncrm.importWizard.fields.emailPrimary"), value: "email_primary" },
          { title: this.$t("ncrm.importWizard.fields.phonePrimary"), value: "phone_primary" },
          { title: this.$t("ncrm.importWizard.fields.source"), value: "source" },
          { title: "ID", value: "id" },
        ];
      }
      return [
        { title: this.$t("ncrm.importWizard.fields.oldReference"), value: "old_reference" },
        { title: this.$t("ncrm.importWizard.fields.companyName"), value: "name" },
        { title: this.$t("ncrm.importWizard.fields.emailPrimary"), value: "email_primary" },
        { title: this.$t("ncrm.importWizard.fields.phonePrimary"), value: "phone_primary" },
        { title: this.$t("ncrm.importWizard.fields.businessCategory"), value: "business_category" },
        { title: this.$t("ncrm.importWizard.fields.source"), value: "source" },
        { title: "ID", value: "id" },
      ];
    },
    activityDedupeFieldOptions() {
      return [
        { title: this.$t("ncrm.importWizard.fields.oldReference"), value: "old_reference" },
        { title: this.$t("ncrm.importWizard.fields.title"), value: "title" },
        { title: this.$t("ncrm.importWizard.fields.content"), value: "content" },
        { title: this.$t("ncrm.importWizard.fields.happenedAt"), value: "happened_at" },
        { title: this.$t("ncrm.importWizard.fields.dueAt"), value: "due_at" },
        { title: this.$t("ncrm.importWizard.fields.legacySource"), value: "legacy_source" },
        { title: this.$t("ncrm.importWizard.fields.legacyAuthorName"), value: "legacy_author_name" },
        { title: "ID", value: "id" },
      ];
    },
    activityMappingGuideRows() {
      return [
        {
          key: "messageId",
          fileColumn: "id",
          mappingTarget: this.$t("ncrm.importWizard.fields.oldReference"),
          purpose: this.$t("ncrm.importWizard.activityMappingGuide.rows.messageId"),
        },
        {
          key: "content",
          fileColumn: "Comment",
          mappingTarget: this.$t("ncrm.importWizard.fields.content"),
          purpose: this.$t("ncrm.importWizard.activityMappingGuide.rows.content"),
        },
        {
          key: "happenedAt",
          fileColumn: "Added_at",
          mappingTarget: this.$t("ncrm.importWizard.fields.happenedAt"),
          purpose: this.$t("ncrm.importWizard.activityMappingGuide.rows.happenedAt"),
        },
        {
          key: "author",
          fileColumn: "Added_by",
          mappingTarget: this.$t("ncrm.importWizard.fields.legacyAuthorName"),
          purpose: this.$t("ncrm.importWizard.activityMappingGuide.rows.author"),
        },
        {
          key: "matchId",
          fileColumn: "id_foreign",
          mappingTarget: this.$t("ncrm.importWizard.activityMappingGuide.leaveUnmapped"),
          purpose: this.$t("ncrm.importWizard.activityMappingGuide.rows.matchIdForeign"),
        },
      ];
    },
    activityOptionsGuideRows() {
      return [
        {
          key: "targetType",
          optionLabel: this.$t("ncrm.importWizard.activityTargetEntityType"),
          purpose: this.$t("ncrm.importWizard.activityOptionsGuide.rows.targetType"),
          recommended:
            this.activityTargetEntityType === "person"
              ? this.$t("ncrm.importWizard.activityOptionsGuide.recommended.person")
              : this.$t("ncrm.importWizard.activityOptionsGuide.recommended.company"),
        },
        {
          key: "matchSource",
          optionLabel: this.$t("ncrm.importWizard.activityMatchSourceColumn"),
          purpose: this.$t("ncrm.importWizard.activityOptionsGuide.rows.matchSource"),
          recommended: "id_foreign",
        },
        {
          key: "matchInternal",
          optionLabel: this.$t("ncrm.importWizard.activityMatchInternalField"),
          purpose: this.$t("ncrm.importWizard.activityOptionsGuide.rows.matchInternal"),
          recommended: this.$t("ncrm.importWizard.fields.oldReference"),
        },
        {
          key: "dedupeSource",
          optionLabel: this.$t("ncrm.importWizard.activityDedupeSourceColumn"),
          purpose: this.$t("ncrm.importWizard.activityOptionsGuide.rows.dedupeSource"),
          recommended: "id",
        },
        {
          key: "dedupeInternal",
          optionLabel: this.$t("ncrm.importWizard.activityDedupeInternalField"),
          purpose: this.$t("ncrm.importWizard.activityOptionsGuide.rows.dedupeInternal"),
          recommended: this.$t("ncrm.importWizard.fields.oldReference"),
        },
      ];
    },
    previewHeaders() {
      if (this.importType === "activity") {
        return [
          { title: this.$t("ncrm.importWizard.previewHeaders.content"), key: "content" },
          { title: this.$t("ncrm.importWizard.previewHeaders.title"), key: "title" },
          { title: this.$t("ncrm.importWizard.previewHeaders.source"), key: "source" },
          { title: this.$t("ncrm.importWizard.previewHeaders.match"), key: "match" },
        ];
      }
      const fields = this.previewContactFieldsForDisplay;
      if (!fields.length) {
        return [{ title: this.$t("ncrm.importWizard.previewHeaders.source"), key: "source" }];
      }
      return fields.map((field) => ({
        title: this.resolveImportMappingFieldLabel(field),
        key: field,
      }));
    },
    previewContactFieldsForDisplay() {
      if (!["company", "person"].includes(this.importType)) return [];
      const selected = [];
      const seen = new Set();
      (this.mappingRows || []).forEach((row) => {
        const field = String(row?.mappedField || "").trim();
        if (!field) return;
        if (field === this.createCustomFieldOptionValue) return;
        if (field.startsWith("custom_field_")) return;
        if (seen.has(field)) return;
        seen.add(field);
        selected.push(field);
      });
      return selected.slice(0, 4);
    },
    conflictOptions() {
      return [
        { title: this.$t("ncrm.importWizard.conflicts.perField"), value: "per_field" },
        { title: this.$t("ncrm.importWizard.conflicts.skip"), value: "skip" },
        { title: this.$t("ncrm.importWizard.conflicts.update"), value: "update" },
        { title: this.$t("ncrm.importWizard.conflicts.merge"), value: "merge" },
        { title: this.$t("ncrm.importWizard.conflicts.fillEmpty"), value: "fill_empty" },
        { title: this.$t("ncrm.importWizard.conflicts.duplicate"), value: "duplicate" },
      ];
    },
    activeType() {
      return this.activeTab === "companies" ? "company" : "person";
    },
    hasActiveAdvancedFilters() {
      return hasContactsAdvancedFilters(this.advancedFilters);
    },
    addAdvancedFields() {
      const keys =
        this.activeTab === "companies"
          ? COMPANY_ADD_ADVANCED_FIELD_KEYS
          : PERSON_ADD_ADVANCED_FIELD_KEYS;
      const labelOptions = buildNcrmContactLabelOptions(this.$t.bind(this));
      return keys.map((key) => {
        const label = this.resolveAddFieldLabel(key);
        return isNcrmContactLabelField(key)
          ? { key, component: "v-select", props: { label, variant: "outlined", density: "comfortable", items: labelOptions, itemTitle: "label", itemValue: "value", clearable: true, menuProps: { maxHeight: 320, zIndex: 2900 } } }
          : { key, component: "v-text-field", props: { label, variant: "outlined", density: "comfortable" } };
      });
    },
  },
  watch: {
    "$route.query.tab"(value) {
      this.applyRouteTab(value);
    },
    activeTab() {
      this.syncRouteTab();
      this.sortBy = createDefaultContactsSortBy();
      this.clearSelectedContacts();
      this.advancedFilterOpen = false;
      this.advancedFilters = readContactsAdvancedFilters(this.activeType);
      if (this.page !== 1) {
        this.page = 1;
        return;
      }
      this.fetchContacts();
    },
    page() {
      this.fetchContacts();
    },
    itemsPerPage() {
      this.page = 1;
      this.fetchContacts();
    },
    search() {
      this.page = 1;
      this.fetchContacts();
    },
    sortBy: {
      deep: true,
      handler() {
        if (this.page !== 1) {
          this.page = 1;
          return;
        }
        this.fetchContacts();
      },
    },
    companySearchQuery() {
      if (!this.addDialog || this.activeTab !== "people") return;
      const normalizedSearch = String(this.companySearchQuery || "").trim();
      const selectedId = Number(this.addForm?.ncrmCompanyId || 0);
      if (normalizedSearch && selectedId > 0) {
        const selectedOption = (this.companyOptions || []).find(
          (entry) => Number(entry?.value) === selectedId
        );
        const selectedTitle = String(selectedOption?.title || "").trim();
        if (selectedTitle && normalizedSearch.toLowerCase() === selectedTitle.toLowerCase()) {
          return;
        }
      }
      this.scheduleCompanyOptionsSearch();
    },
    importType(next) {
      if (!this.importDialog) return;
      this.contactMatchSourceColumn = "";
      this.contactMatchInternalField = "";
      if (next === "activity") {
        this.activityTargetEntityType = "company";
        this.activityMatchSourceColumn = "";
        this.activityMatchInternalField = "";
        this.activityDedupeSourceColumn = "";
        this.activityDedupeInternalField = "";
        return;
      }
      this.loadCustomFieldDefinitions(next);
    },
    activityTargetEntityType() {
      this.activityMatchInternalField = "";
    },
  },
  mounted() {
    this.syncRouteTab();
    this.fetchContacts();
  },
  beforeUnmount() {
    if (this.companySearchDebounceId) {
      clearTimeout(this.companySearchDebounceId);
      this.companySearchDebounceId = null;
    }
  },
  methods: {
    viewPreferencePageKey() {
      return resolveViewPreferencePageKey(this.$route, CONTACTS_PAGE_KEY_FALLBACK);
    },
    syncRouteTab() {
      const currentQuery = this.$route?.query || {};
      const currentTab = resolveNcrmContactsTab(currentQuery.tab, "");
      if (currentTab === this.activeTab) return;
      const nextQuery = {
        ...currentQuery,
        tab: this.activeTab,
      };
      this.$router.replace({ query: nextQuery }).catch(() => {});
    },
    applyRouteTab(value) {
      const tab = resolveNcrmContactsTab(value, this.preferredTab || this.activeTab);
      if (tab !== this.activeTab) {
        this.activeTab = tab;
        return true;
      }
      return false;
    },
    restorePreferredTab() {
      const preferredTab =
        loadPreferredView({
          pageKey: this.viewPreferencePageKey(),
          items: CONTACT_VIEW_ITEMS,
          fallback: "companies",
        }) || "companies";
      this.preferredTab = preferredTab;
      return this.applyRouteTab(this.$route?.query?.tab || preferredTab);
    },
    setPreferredTab(value) {
      const preferredTab = savePreferredView({
        pageKey: this.viewPreferencePageKey(),
        value,
        items: CONTACT_VIEW_ITEMS,
      });
      if (!preferredTab) return;
      this.preferredTab = preferredTab;
      if (preferredTab !== this.activeTab) {
        this.activeTab = preferredTab;
      }
    },
    handleImport() {
      this.openImportDialog();
    },
    handleAdd() {
      this.openAddDialog();
    },
    handleContactRowClick(_event, payload) {
      if (this.isSelectionClick(_event)) return;
      const rawItem = payload?.item?.raw ?? payload?.item;
      const route = resolveNcrmContactDetailsRoute(this.activeType, rawItem?.id);
      if (!route) return;
      this.$router.push(route);
    },
    goToMailjetTab(item) {
      const route = resolveNcrmContactDetailsRoute(this.activeType, item?.id);
      if (!route) return;
      this.$router.push({
        ...(typeof route === "string" ? { path: route } : route),
        query: { ...(route?.query || {}), tab: "mailjet" },
      });
    },
    privacyChipProps(item) {
      return {
        state: item?.privacy_state, breakdown: item?.privacy_breakdown,
        total: item?.privacy_contact_points_total,
        unclassified: item?.privacy_contact_points_unclassified,
      };
    },
    goToDataProtectionTab(item) {
      const route = resolveNcrmContactDetailsRoute(this.activeType, item?.id);
      if (!route) return;
      this.$router.push({
        ...(typeof route === "string" ? { path: route } : route),
        query: { ...(route?.query || {}), tab: "data-protection" },
      });
    },
    handleAdvancedFiltersUpdate(filters) {
      this.advancedFilters = writeContactsAdvancedFilters(this.activeType, filters);
      if (this.page !== 1) {
        this.page = 1;
        return;
      }
      this.fetchContacts();
    },
    removeAdvancedFilterCondition(index) {
      const current = normalizeContactsAdvancedFilters(this.advancedFilters);
      const conditions = current.conditions.filter((_, i) => i !== index);
      this.handleAdvancedFiltersUpdate({ logic: current.logic, conditions });
    },
    clearAdvancedFilters() {
      this.handleAdvancedFiltersUpdate({ logic: "and", conditions: [] });
    },
    scheduleCompanyOptionsSearch() {
      if (this.companySearchDebounceId) {
        clearTimeout(this.companySearchDebounceId);
      }
      this.companySearchDebounceId = setTimeout(() => {
        this.loadCompanyOptions({ search: this.companySearchQuery });
      }, 260);
    },
    async loadCompanyOptions({ search = "" } = {}) {
      const requestSeq = ++this.companyOptionsRequestSeq;
      const normalizedSearch = String(search || "").trim();
      this.companyOptionsLoading = true;
      try {
        const { data } = await axios.get("/ncrm/contacts", {
          params: {
            type: "company",
            page: 1,
            perPage: normalizedSearch ? 50 : 200,
            search: normalizedSearch || undefined,
          },
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        const nextOptions = items.map((row) => ({
          title: row.name,
          value: row.id,
        }));
        if (requestSeq !== this.companyOptionsRequestSeq) return;
        const selectedId = Number(this.addForm?.ncrmCompanyId || 0);
        const hasSelected = nextOptions.some((entry) => Number(entry?.value) === selectedId);
        if (selectedId > 0 && !hasSelected) {
          const fallback = (this.companyOptions || []).find(
            (entry) => Number(entry?.value) === selectedId
          );
          if (fallback) {
            nextOptions.unshift(fallback);
          }
        }
        this.companyOptions = nextOptions;
      } catch (error) {
        console.error("[ncrm] load company options failed:", error);
      } finally {
        if (requestSeq === this.companyOptionsRequestSeq) {
          this.companyOptionsLoading = false;
        }
      }
    },
    ensureCompanyOptionsLoaded() {
      if (this.activeTab !== "people" || this.companyOptionsLoading) return;
      if (Array.isArray(this.companyOptions) && this.companyOptions.length > 0) return;
      this.loadCompanyOptions({ search: this.companySearchQuery });
    },
    async loadCustomFieldDefinitions(entityType = this.activeType) {
      this.customFieldsLoading = true;
      try {
        const { data } = await axios.get("/ncrm/custom-fields", {
          params: {
            entityType,
          },
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        this.customFieldDefinitions = {
          ...this.customFieldDefinitions,
          [entityType]: items.map((item) => ({
            id: item.id,
            key: item.key,
            label: item.label,
          })),
        };
      } catch (error) {
        console.error("[ncrm] load custom fields failed:", error);
        this.snackbar.message = this.$t("ncrm.contacts.form.customFieldsLoadError");
        this.snackbar.show = true;
      } finally {
        this.customFieldsLoading = false;
      }
    },
    openAddDialog() {
      this.resetAddForm();
      this.addDialog = true;
      if (this.activeTab !== "companies") {
        this.loadCompanyOptions({ search: "" });
      }
    },
    closeAddDialog() {
      this.addDialog = false;
      this.resetAddForm();
    },
    resetAddForm() {
      if (this.companySearchDebounceId) {
        clearTimeout(this.companySearchDebounceId);
        this.companySearchDebounceId = null;
      }
      this.addForm = {
        firstName: "",
        lastName: "",
        emailPrimary: "",
        phonePrimary: "",
        ncrmCompanyId: null,
        name: "",
        businessCategory: "",
        source: "",
        old_reference: "",
        legal_name: "",
        fiscal_code: "",
        vat_number: "",
        registration_number: "",
        iban: "",
        industry: "",
        company_size: "",
        employees_count: "",
        annual_revenue: "",
        email_secondary: "",
        email_tertiary: "",
        email_primary_label: "",
        email_secondary_label: "",
        email_tertiary_label: "",
        phone_secondary: "",
        phone_tertiary: "",
        phone_primary_label: "",
        phone_secondary_label: "",
        phone_tertiary_label: "",
        website: "",
        address_text: "", country: "",
        linkedin: "",
        facebook: "",
        instagram: "",
        administrator_name: "",
        sales_manager_id: "",
        tags: "",
        notes: "",
        owner_user_id: "",
        position: "",
        department: "",
      };
      this.companySearchQuery = "";
      this.addShowAllFields = false;
      this.addNewCustomFields = [];
      this.addNewCustomFieldSeq = 0;
    },
    expandAddFormFields() {
      this.addShowAllFields = true;
    },
    addAddCustomFieldRow() {
      this.addNewCustomFieldSeq += 1;
      this.addNewCustomFields = [
        ...(this.addNewCustomFields || []),
        {
          temp_id: this.addNewCustomFieldSeq,
          label: "",
          value: "",
        },
      ];
    },
    removeAddCustomFieldRow(tempId) {
      const normalizedId = Number(tempId);
      if (!Number.isFinite(normalizedId) || normalizedId <= 0) return;
      this.addNewCustomFields = (this.addNewCustomFields || []).filter(
        (entry) => Number(entry?.temp_id) !== normalizedId
      );
    },
    buildNewCustomFieldsFromAdd() {
      const rows = Array.isArray(this.addNewCustomFields) ? this.addNewCustomFields : [];
      const items = [];
      for (const row of rows) {
        const label = String(row?.label || "").trim();
        const value = String(row?.value || "").trim();
        if (!label && !value) continue;
        if (!label) {
          return {
            items: [],
            error: this.$t("ncrm.contacts.details.customFieldEditor.nameRequired"),
          };
        }
        items.push({ label, value });
      }
      return {
        items,
        error: "",
      };
    },
    buildAdditionalAddPayload(fieldKeys = []) {
      return (fieldKeys || []).reduce((acc, key) => {
        const normalizedKey = String(key || "").trim();
        if (!normalizedKey) return acc;
        acc[normalizedKey] = String(this.addForm?.[normalizedKey] ?? "").trim();
        return acc;
      }, {});
    },
    humanizeFieldKey(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return "";
      return normalized
        .replace(/_/g, " ")
        .replace(/\bid\b/g, "ID")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    resolveImportMappingFieldLabel(key) {
      const normalizedKey = String(key || "").trim();
      if (!normalizedKey) return "";
      const translationKey = IMPORT_WIZARD_FIELD_TRANSLATION_KEYS[normalizedKey];
      if (translationKey) {
        return this.$t(translationKey);
      }
      return this.humanizeFieldKey(normalizedKey);
    },
    mappingFieldOptionsForRow(row) {
      const currentRow = row && typeof row === "object" && row.raw ? row.raw : row;
      const currentField = String(currentRow?.mappedField || "").trim();
      const currentColumn = String(currentRow?.column || "").trim();
      const usedByOthers = new Set();
      (this.mappingRows || []).forEach((entry) => {
        if (!entry || typeof entry !== "object") return;
        const fieldValue = String(entry.mappedField || "").trim();
        if (!fieldValue) return;
        if (currentColumn && String(entry.column || "").trim() === currentColumn) return;
        usedByOthers.add(fieldValue);
      });
      return (this.mappingFieldOptions || []).filter((option) => {
        const value = String(option?.value || "").trim();
        if (!value) return true;
        if (value === this.createCustomFieldOptionValue) return true;
        if (currentField && value === currentField) return true;
        return !usedByOthers.has(value);
      });
    },
    resolveAddFieldLabel(key) {
      const normalizedKey = String(key || "").trim();
      if (!normalizedKey) return "";
      const labelMap =
        this.activeTab === "companies"
          ? COMPANY_ADD_FIELD_TRANSLATION_KEYS
          : PERSON_ADD_FIELD_TRANSLATION_KEYS;
      const translationKey = labelMap[normalizedKey];
      if (translationKey) {
        return this.$t(translationKey);
      }
      return this.humanizeFieldKey(normalizedKey);
    },
    async submitAdd() {
      if (this.activeTab === "companies") {
        if (!this.addForm.name) {
          this.snackbar.message = this.$t("ncrm.contacts.form.companyNameRequired");
          this.snackbar.show = true;
          return;
        }
      } else if (!this.addForm.lastName) {
        this.snackbar.message = this.$t("ncrm.contacts.form.lastNameRequired");
        this.snackbar.show = true;
        return;
      }
      const { items: newCustomFields, error: newCustomFieldsError } = this.buildNewCustomFieldsFromAdd();
      if (newCustomFieldsError) {
        this.snackbar.message = newCustomFieldsError;
        this.snackbar.show = true;
        return;
      }
      this.addLoading = true;
      try {
        const payload =
          this.activeTab === "companies"
            ? {
                type: "company",
                name: this.addForm.name,
                business_category: this.addForm.businessCategory,
                email_primary: this.addForm.emailPrimary,
                phone_primary: this.addForm.phonePrimary,
                source: this.addForm.source,
              }
            : {
                type: "person",
                first_name: this.addForm.firstName,
                last_name: this.addForm.lastName,
                email_primary: this.addForm.emailPrimary,
                phone_primary: this.addForm.phonePrimary,
                source: this.addForm.source,
                ncrm_company_id: this.addForm.ncrmCompanyId,
              };
        if (this.addShowAllFields) {
          const extraPayload =
            this.activeTab === "companies"
              ? this.buildAdditionalAddPayload(COMPANY_ADD_ADVANCED_FIELD_KEYS)
              : this.buildAdditionalAddPayload(PERSON_ADD_ADVANCED_FIELD_KEYS);
          Object.assign(payload, extraPayload);
        }
        const customFieldsPayload = [];
        for (const field of newCustomFields) {
          const { data } = await axios.post("/ncrm/custom-fields", {
            entityType: this.activeType,
            label: field.label,
          });
          const fieldDefinitionId = Number(data?.id);
          if (!Number.isFinite(fieldDefinitionId) || fieldDefinitionId <= 0) {
            throw new Error(this.$t("ncrm.contacts.details.customFieldEditor.createInvalidResponse"));
          }
          if (field.value) {
            customFieldsPayload.push({
              field_definition_id: fieldDefinitionId,
              value_text: field.value,
            });
          }
        }
        if (customFieldsPayload.length) {
          payload.custom_fields = customFieldsPayload;
        }
        await axios.post("/ncrm/contacts", payload);
        this.snackbar.message = this.$t("ncrm.contacts.form.saved");
        this.snackbar.show = true;
        this.closeAddDialog();
        await this.fetchContacts();
      } catch (error) {
        console.error("[ncrm] add contact failed:", error);
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.contacts.form.saveError");
        this.snackbar.show = true;
      } finally {
        this.addLoading = false;
      }
    },
    openImportDialog() {
      this.importDialog = true;
      this.step = 1;
      if (this.importType !== "activity") {
        this.loadCustomFieldDefinitions(this.importType);
      }
    },
    closeImportDialog() {
      this.importDialog = false;
      this.resetImportState();
    },
    handleMappingFieldChange(row, value) {
      if (value !== this.createCustomFieldOptionValue) {
        return;
      }
      if (row && Object.prototype.hasOwnProperty.call(row, "mappedField")) {
        row.mappedField = null;
      }
      this.openImportCustomFieldDialog(row?.column || null);
    },
    openImportCustomFieldDialog(column) {
      this.importCustomFieldDialog = {
        show: true,
        saving: false,
        label: "",
        targetColumn: column || null,
      };
    },
    closeImportCustomFieldDialog() {
      this.importCustomFieldDialog = {
        ...this.importCustomFieldDialog,
        show: false,
        saving: false,
        label: "",
        targetColumn: null,
      };
    },
    async submitImportCustomField() {
      const label = String(this.importCustomFieldDialog.label || "").trim();
      if (!label) {
        this.snackbar.message = this.$t("ncrm.importWizard.createCustomFieldLabelRequired");
        this.snackbar.show = true;
        return;
      }
      this.importCustomFieldDialog = {
        ...this.importCustomFieldDialog,
        saving: true,
      };
      try {
        const { data } = await axios.post("/ncrm/custom-fields", {
          entityType: this.importType,
          label,
        });
        const nextField = {
          id: data.id,
          key: data.key,
          label: data.label,
        };
        const existing = this.customFieldDefinitions[this.importType] || [];
        this.customFieldDefinitions = {
          ...this.customFieldDefinitions,
          [this.importType]: [...existing, nextField],
        };
        const existingCreatedIds = this.importCreatedCustomFieldIds[this.importType] || [];
        this.importCreatedCustomFieldIds = {
          ...this.importCreatedCustomFieldIds,
          [this.importType]: existingCreatedIds.includes(nextField.id)
            ? existingCreatedIds
            : [...existingCreatedIds, nextField.id],
        };
        if (this.importCustomFieldDialog.targetColumn) {
          const mappedValue = `custom_field_${nextField.id}`;
          this.mappingRows = this.mappingRows.map((entry) =>
            entry.column === this.importCustomFieldDialog.targetColumn
              ? { ...entry, mappedField: mappedValue }
              : entry
          );
        }
        this.snackbar.message = this.$t("ncrm.importWizard.createCustomFieldSuccess");
        this.snackbar.show = true;
        this.closeImportCustomFieldDialog();
      } catch (error) {
        console.error("[ncrm] create import custom field failed:", error);
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importWizard.createCustomFieldError");
        this.snackbar.show = true;
        this.importCustomFieldDialog = {
          ...this.importCustomFieldDialog,
          saving: false,
        };
      }
    },
    nextStep() {
      if (this.step === 2) {
        this.handleFileStep();
        return;
      }
      if (this.step === 4) {
        this.handlePreviewStep();
        return;
      }
      if (this.step < 6) {
        this.step += 1;
        return;
      }
      // DPIA A3 / B1: gate-ul cere un răspuns, nu unul anume — `undocumented`
      // trece. Backendul refuză oricum cererea fără el.
      if (!isProvenanceComplete(this.importProvenance)) {
        this.provenanceTouched = true;
        this.snackbar.message = this.$t("ncrm.importWizard.provenance.blocked");
        this.snackbar.show = true;
        return;
      }
      this.executeImport();
    },
    prevStep() {
      if (this.step > 1) this.step -= 1;
    },
    autoMap() {
      const allowed = this.getAllowedMappingTargets();
      const used = new Set(
        this.mappingRows.map((row) => row.mappedField).filter(Boolean)
      );
      let filled = 0;
      this.mappingRows = this.mappingRows.map((row) => {
        if (row.mappedField) return row;
        const guessed = this.guessFieldFromHeader(row.column, allowed, used);
        if (!guessed) return row;
        used.add(guessed);
        filled += 1;
        return { ...row, mappedField: guessed };
      });
      this.snackbar.message = this.$t("ncrm.importWizard.autoMapDone", { count: filled });
      this.snackbar.show = true;
    },
    resetMap() {
      this.mappingRows = this.mappingRows.map((row) => ({ ...row, mappedField: null }));
    },
    resolvePerPage() {
      if (this.itemsPerPage === -1) return -1;
      return this.itemsPerPage;
    },
    normalizeHeaderValue(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/[^\p{L}\p{N}\s]/gu, " ")
        .replace(/\s+/g, " ")
        .trim();
    },
    getAllowedMappingTargets() {
      if (this.importType === "activity") {
        return new Set([
          "content",
          "title",
          "happened_at",
          "due_at",
          "old_reference",
          "legacy_source",
          "legacy_author_name",
          "owner_user_id",
          "legacy_payload",
          "type_payload",
        ]);
      }
      const base = new Set([
        "email_primary",
        "email_secondary",
        "email_tertiary",
        "phone_primary",
        "phone_secondary",
        "phone_tertiary",
        "address_text", "country",
        "source",
        "old_reference",
      ]);
      if (this.importType === "company") {
        base.add("name");
        base.add("business_category");
        base.add("business_is_active");
      } else {
        base.add("first_name");
        base.add("last_name");
      }
      return base;
    },
    guessFieldFromHeader(header, allowed, used) {
      const normalized = this.normalizeHeaderValue(header);
      if (!normalized) return null;
      const pick = (field) => {
        if (!allowed.has(field) || used.has(field)) return null;
        return field;
      };
      const looksLikeMailingFlag = /(mailjet|mailchimp|newsletter|subscribed|unsubscribe|mailing list)/.test(
        normalized
      );
      const hasEmail = !looksLikeMailingFlag && /(email|e mail|e-mail|mail)/.test(normalized);
      if (hasEmail) {
        if (/(3|third|tertiary|tertiar)/.test(normalized)) return pick("email_tertiary");
        if (/(2|second|secondary|secundar)/.test(normalized)) return pick("email_secondary");
        if (/(1|first|primary|main|principal|primar)/.test(normalized) || normalized === "email") {
          return pick("email_primary");
        }
        return pick("email_primary");
      }
      const hasPhone = /(phone|telefon|tel|mobile|gsm)/.test(normalized);
      if (hasPhone) {
        if (/(3|third|tertiary|tertiar)/.test(normalized)) return pick("phone_tertiary");
        if (/(2|second|secondary|secundar)/.test(normalized)) return pick("phone_secondary");
        if (/(1|first|primary|main|principal|primar)/.test(normalized) || normalized === "phone") {
          return pick("phone_primary");
        }
        return pick("phone_primary");
      }
      if (/(first name|firstname|prenume|given name)/.test(normalized)) {
        return pick("first_name");
      }
      if (/(message|mesaj|note|nota|body|text|comment|comentariu|content)/.test(normalized)) {
        return pick("content");
      }
      if (/(subject|subiect|title|titlu)/.test(normalized)) {
        return pick("title");
      }
      if (/(date|data|created at|timestamp|time|sent at|happened)/.test(normalized)) {
        return pick("happened_at");
      }
      if (/(due|deadline|scadent|follow up)/.test(normalized)) {
        return pick("due_at");
      }
      if (/(author|added by|sender|from|user|operator)/.test(normalized)) {
        return pick("legacy_author_name");
      }
      if (/(platform|channel|legacy source|legacy|sursa)/.test(normalized)) {
        return pick("legacy_source");
      }
      if (/(last name|lastname|surname|nume|familie)/.test(normalized)) {
        return pick("last_name");
      }
      if (normalized === "name" || /(full name|fullname|nume complet)/.test(normalized)) {
        return pick("name") || pick("last_name");
      }
      if (/(company|companie|firma|organization|organisation|business name|denumire)/.test(normalized)) {
        return pick("name");
      }
      if (/(business category|categorie|category|industry|sector)/.test(normalized)) {
        return pick("business_category");
      }
      if (/(business active|is active|activ|inactive|inactiv|closed|inchis|închis)/.test(normalized)) {
        return pick("business_is_active");
      }
      if (/(old reference|reference|legacy id|external id|old id)/.test(normalized)) {
        return pick("old_reference");
      }
      if (/(country|tara|țara|ţara|stat|страна)/.test(normalized)) return pick("country");
      if (/(address|adresa|street|strada|location|locatie)/.test(normalized)) {
        return pick("address_text");
      }
      if (/(source|sursa|lead source|origine)/.test(normalized)) {
        return pick("source");
      }
      return null;
    },
    getImportFile() {
      if (Array.isArray(this.importFile)) {
        return this.importFile[0] || null;
      }
      return this.importFile;
    },
    isSupportedFile(file) {
      if (!file?.name) return false;
      const lower = file.name.toLowerCase();
      return (
        lower.endsWith(".csv") ||
        lower.endsWith(".xlsx") ||
        lower.endsWith(".json") ||
        lower.endsWith(".sql")
      );
    },
    buildMappingPayload() {
      const mapping = {};
      this.mappingRows.forEach((row) => {
        if (!row?.mappedField || !row?.column) return;
        if (!(row.mappedField in mapping)) {
          mapping[row.mappedField] = row.column;
        }
      });
      return mapping;
    },
    buildPreviewRowsFromMapped() {
      if (!Array.isArray(this.previewMappedRows) || !this.previewMappedRows.length) {
        this.previewRows = [];
        return;
      }
      this.previewRows = this.previewMappedRows.map((entry) => {
        const mapped = entry?.mapped || {};
        if (this.importType === "activity") {
          const matchValueRaw = this.activityMatchSourceColumn
            ? entry?.raw?.[this.activityMatchSourceColumn] ?? ""
            : "";
          const matchValue = this.applyLegacyReferencePrefixForDisplay(
            matchValueRaw,
            this.activityMatchInternalField
          );
          return {
            content: mapped.content || "",
            title: mapped.title || "",
            source: mapped.legacy_source || "",
            match: matchValue === null || matchValue === undefined ? "" : String(matchValue),
          };
        }
        if (this.importType === "company") {
          const selectedFields = this.previewContactFieldsForDisplay;
          if (!selectedFields.length) {
            return {
              source: mapped.source || "",
            };
          }
          return selectedFields.reduce((acc, field) => {
            const value = mapped?.[field];
            if (value === null || value === undefined) {
              acc[field] = "";
            } else if (typeof value === "object") {
              try {
                acc[field] = JSON.stringify(value);
              } catch (_) {
                acc[field] = String(value);
              }
            } else {
              acc[field] = String(value);
            }
            return acc;
          }, {});
        }
        const selectedFields = this.previewContactFieldsForDisplay;
        if (!selectedFields.length) {
          return {
            source: mapped.source || "",
          };
        }
        return selectedFields.reduce((acc, field) => {
          const value = mapped?.[field];
          if (value === null || value === undefined) {
            acc[field] = "";
          } else if (typeof value === "object") {
            try {
              acc[field] = JSON.stringify(value);
            } catch (_) {
              acc[field] = String(value);
            }
          } else {
            acc[field] = String(value);
          }
          return acc;
        }, {});
      });
    },
    ensureActivityImportConfig() {
      if (this.importType !== "activity") return true;
      const required = [
        this.activityTargetEntityType,
        this.activityMatchSourceColumn,
        this.activityMatchInternalField,
        this.activityDedupeSourceColumn,
        this.activityDedupeInternalField,
      ];
      const isValid = required.every((value) => String(value || "").trim().length > 0);
      if (!isValid) {
        this.snackbar.message = this.$t("ncrm.importWizard.activityConfigMissing");
        this.snackbar.show = true;
        return false;
      }
      return true;
    },
    ensureContactMatchConfig() {
      if (!["company", "person"].includes(this.importType)) return true;
      const sourceColumn = String(this.contactMatchSourceColumn || "").trim();
      const internalField = String(this.contactMatchInternalField || "").trim();
      if (!sourceColumn && !internalField) return true;
      if (sourceColumn && internalField) return true;
      this.snackbar.message = this.$t("ncrm.importWizard.contactMatchConfigIncomplete");
      this.snackbar.show = true;
      return false;
    },
    applyLegacyReferencePrefixForDisplay(value, internalField) {
      const field = String(internalField || "").trim().toLowerCase();
      if (field !== "old_reference") {
        if (value === null || value === undefined) return "";
        return String(value);
      }
      const normalizedValue = String(value ?? "").trim();
      if (!normalizedValue) return "";
      const prefix = normalizeLegacyReferencePrefix(this.legacyReferencePrefix);
      if (!prefix) return normalizedValue;
      const withSeparator = `${prefix}:`;
      if (normalizedValue.toLowerCase().startsWith(withSeparator.toLowerCase())) {
        return normalizedValue;
      }
      return `${withSeparator}${normalizedValue}`;
    },
    async handleFileStep() {
      const file = this.getImportFile();
      if (!file) {
        this.snackbar.message = this.$t("ncrm.importWizard.fileMissing");
        this.snackbar.show = true;
        return;
      }
      if (!this.isSupportedFile(file)) {
        this.snackbar.message = this.$t("ncrm.importWizard.fileTypeInvalid");
        this.snackbar.show = true;
        return;
      }
      await this.loadPreviewHeaders();
      if (this.mappingRows.length) {
        this.step = 3;
      }
    },
    async handlePreviewStep() {
      if (!this.ensureActivityImportConfig()) {
        return;
      }
      if (!this.ensureContactMatchConfig()) {
        return;
      }
      await this.loadPreviewMapped();
      if (this.previewReady) {
        this.step = 5;
      }
    },
    async loadPreviewHeaders() {
      const file = this.getImportFile();
      if (!file) return;
      this.previewLoading = true;
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("importType", this.importType);
        if (this.importName) formData.append("importName", this.importName);
        if (this.importSource) formData.append("importSource", this.importSource);
        if (this.importTags) formData.append("tags", this.importTags);
        if (this.businessCategory) formData.append("businessCategory", this.businessCategory);
        appendImportConfig(formData, this);
        const { data } = await axios.post("/ncrm/import/preview", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const headers = Array.isArray(data?.headers) ? data.headers : [];
        this.previewRawRows = Array.isArray(data?.previewRows) ? data.previewRows : [];
        const previousMapping = new Map(
          this.mappingRows.map((row) => [row.column, row.mappedField])
        );
        this.mappingRows = headers.map((header) => ({
          column: header,
          sample: this.previewRawRows[0]?.[header] ?? "",
          mappedField: previousMapping.get(header) || null,
        }));
      } catch (error) {
        console.error("[ncrm] preview headers failed:", error);
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importWizard.previewError");
        this.snackbar.show = true;
      } finally {
        this.previewLoading = false;
      }
    },
    async loadPreviewMapped() {
      const file = this.getImportFile();
      if (!file) return;
      if (!this.isSupportedFile(file)) {
        this.snackbar.message = this.$t("ncrm.importWizard.fileTypeInvalid");
        this.snackbar.show = true;
        return;
      }
      if (!this.ensureActivityImportConfig()) {
        return;
      }
      if (!this.ensureContactMatchConfig()) {
        return;
      }
      const mapping = this.buildMappingPayload();
      if (!Object.keys(mapping).length) {
        this.snackbar.message = this.$t("ncrm.importWizard.mappingMissing");
        this.snackbar.show = true;
        return;
      }
      this.previewReady = false;
      this.previewLoading = true;
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("importType", this.importType);
        formData.append("mapping", JSON.stringify(mapping));
        formData.append("conflictStrategy", this.conflictStrategy);
        if (this.importName) formData.append("importName", this.importName);
        if (this.importSource) formData.append("importSource", this.importSource);
        if (this.importTags) formData.append("tags", this.importTags);
        if (this.businessCategory) formData.append("businessCategory", this.businessCategory);
        appendImportConfig(formData, this);
        const { data } = await axios.post("/ncrm/import/preview", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.previewMappedRows = Array.isArray(data?.previewMapped) ? data.previewMapped : [];
        this.buildPreviewRowsFromMapped();
        this.previewReady = true;
      } catch (error) {
        console.error("[ncrm] preview mapped failed:", error);
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importWizard.previewError");
        this.snackbar.show = true;
      } finally {
        this.previewLoading = false;
      }
    },
    async executeImport() {
      const file = this.getImportFile();
      if (!file) {
        this.snackbar.message = this.$t("ncrm.importWizard.fileMissing");
        this.snackbar.show = true;
        return;
      }
      if (!this.isSupportedFile(file)) {
        this.snackbar.message = this.$t("ncrm.importWizard.fileTypeInvalid");
        this.snackbar.show = true;
        return;
      }
      if (!this.ensureActivityImportConfig()) {
        return;
      }
      if (!this.ensureContactMatchConfig()) {
        return;
      }
      const mapping = this.buildMappingPayload();
      if (!Object.keys(mapping).length) {
        this.snackbar.message = this.$t("ncrm.importWizard.mappingMissing");
        this.snackbar.show = true;
        return;
      }
      this.executeLoading = true;
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("importType", this.importType);
        formData.append("mapping", JSON.stringify(mapping));
        formData.append("conflictStrategy", this.conflictStrategy);
        if (this.importName) formData.append("importName", this.importName);
        if (this.importSource) formData.append("importSource", this.importSource);
        if (this.importTags) formData.append("tags", this.importTags);
        if (this.businessCategory) formData.append("businessCategory", this.businessCategory);
        appendImportConfig(formData, this, { includeProvenance: true });
        const { data } = await axios.post("/ncrm/import/execute", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const batchId = data?.batchId;
        this.snackbar.message = batchId
          ? this.$t("ncrm.importWizard.finishedWithBatch", { id: batchId })
          : this.$t("ncrm.importWizard.finished");
        this.snackbar.show = true;
        this.closeImportDialog();
        await this.fetchContacts();
        if (batchId) {
          this.$router.push(`/ncrm/data-integrations/${batchId}`);
        }
      } catch (error) {
        console.error("[ncrm] execute import failed:", error);
        this.snackbar.message =
          error?.response?.data?.error || this.$t("ncrm.importWizard.executeError");
        this.snackbar.show = true;
      } finally {
        this.executeLoading = false;
      }
    },
    resetImportState() {
      this.importType = "person";
      this.importFile = null;
      this.importName = "";
      this.importProvenance = createEmptyProvenance();
      this.provenanceTouched = false;
      this.importSource = "";
      this.importTags = "";
      this.businessCategory = "";
      this.legacyReferencePrefix = "";
      this.contactMatchSourceColumn = "";
      this.contactMatchInternalField = "";
      this.activityTargetEntityType = "company";
      this.activityMatchSourceColumn = "";
      this.activityMatchInternalField = "";
      this.activityDedupeSourceColumn = "";
      this.activityDedupeInternalField = "";
      this.mappingRows = [];
      this.previewRawRows = [];
      this.previewMappedRows = [];
      this.previewRows = [];
      this.previewLoading = false;
      this.previewReady = false;
      this.executeLoading = false;
      this.importCustomFieldDialog = {
        show: false,
        saving: false,
        label: "",
        targetColumn: null,
      };
      this.importCreatedCustomFieldIds = {
        person: [],
        company: [],
      };
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();
      return `${dd}.${mm}.${yyyy}`;
    },
  },
};
</script>
<style scoped src="./NcrmContacts.styles.css"></style>
